/**
 * RefugeFilter — progressive enhancement for the TMB lodgings.
 *
 * The detailed cards are server-rendered in the prose (SEO). This script reads
 * the filter metadata emitted by the `refuges()` component (keyed by each card's
 * anchor id), reveals the filter bar (hidden by default, so non-JS users keep
 * every card), and toggles the cards IN PLACE — hiding a card's whole block
 * (heading + gallery + text + meta line) and collapsing a stage heading
 * (## Étape N) once all of its cards are filtered out.
 *
 * Logic: AND between axes, OR within an axis.
 */

function buildGroups(byId, parent) {
  // Walk the prose in document order, bucketing each block into the card or the
  // stage heading it belongs to. A card = an <h3 id> known to byId plus every
  // following sibling until the next heading. A stage = an <h2> plus its intro
  // blocks and the cards beneath it.
  const cards = [];
  const stages = [];
  let stage = null;
  let card = null;

  for (const el of parent.children) {
    if (el.tagName === "H2") {
      stage = { heading: el, intro: [], cards: [] };
      stages.push(stage);
      card = null;
    } else if (el.tagName === "H3" && byId.has(el.id)) {
      card = { meta: byId.get(el.id), nodes: [el] };
      cards.push(card);
      if (stage) stage.cards.push(card);
    } else if (card) {
      card.nodes.push(el);
    } else if (stage) {
      stage.intro.push(el);
    }
  }

  // Keep only stages that actually hold lodging cards (skip booking / FAQ / etc.).
  return { cards, stages: stages.filter((s) => s.cards.length) };
}

function setupRoot(root) {
  if (root.dataset.refugesReady) return;
  root.dataset.refugesReady = "1";

  const filters = root.querySelector("[data-refuges-filters]");
  const dataEl = root.querySelector("[data-refuges-data]");
  if (!filters || !dataEl) return;

  let meta;
  try {
    meta = JSON.parse(dataEl.textContent);
  } catch (e) {
    return;
  }
  if (!meta.length) return;

  const byId = new Map(meta.map((m) => [m.id, m]));
  const firstHeading = document.getElementById(meta[0].id);
  if (!firstHeading) return;

  const { cards, stages } = buildGroups(byId, firstHeading.parentElement);
  if (!cards.length) return;

  // The "sommaire" above the stages is a server-rendered list of jump-links (kept
  // for SEO and non-JS users), wrapped by the ShowMore component in `.show-more`.
  // Map each lodging id to its <li> so we can hide the entries whose card is
  // filtered out, keeping the index in sync. Scoped to `.show-more` so we don't
  // grab Pushword's separate floating page TOC (#toc), which links the same ids.
  const tocById = new Map();
  meta.forEach((m) => {
    const link = document.querySelector(`.show-more a[href="#${m.id}"]`);
    if (link) tocById.set(m.id, link.closest("li") || link);
  });

  const pills = Array.from(filters.querySelectorAll(".refuge-pill"));
  const countEl = root.querySelector("[data-refuges-count]");
  const emptyEl = root.querySelector("[data-refuges-empty]");
  const resets = Array.from(root.querySelectorAll("[data-refuges-reset]"));

  // Mobile collapse: the panel opens collapsed (no filter set) and the header
  // toggles the body. On sm+ the body stays open and the toggle is inert.
  const toggle = filters.querySelector("[data-refuges-toggle]");
  const body = filters.querySelector("[data-refuges-body]");
  const badge = filters.querySelector("[data-refuges-badge]");
  const mqMobile = window.matchMedia("(max-width: 639.98px)");
  let mobileOpen = false; // collapsed at load — 0 active filters
  const applyCollapse = () => {
    if (!toggle || !body) return;
    const open = !mqMobile.matches || mobileOpen;
    body.hidden = !open;
    toggle.setAttribute("aria-expanded", open ? "true" : "false");
  };
  if (toggle && body) {
    toggle.addEventListener("click", () => {
      if (!mqMobile.matches) return; // inert on desktop
      mobileOpen = !mobileOpen;
      applyCollapse();
    });
    mqMobile.addEventListener("change", applyCollapse);
  }

  // The map (rendered by pointsMap.js) is a sibling of this root; drive its
  // markers from the same filter. It loads async, so stash the desired state
  // until its applyMarkerFilter() hook exists.
  const mapEl =
    (root.parentElement && root.parentElement.querySelector(".points-map")) ||
    document.querySelector(".points-map");
  const updateMap = (visibleHrefs) => {
    if (!mapEl) return;
    if (typeof mapEl.applyMarkerFilter === "function") {
      mapEl.applyMarkerFilter(visibleHrefs);
    } else {
      mapEl._pendingMarkerFilter = visibleHrefs;
    }
  };

  const active = {}; // { axis: Set(values) }

  const matches = (m) =>
    Object.entries(active).every(([axis, values]) => {
      if (!values.size) return true;
      if (axis === "ouverture") {
        return values.has("precoce") ? m.ouv <= 5 : true;
      }
      if (axis === "ffcam") {
        return values.has(String(m.ffcam));
      }
      return values.has(m[axis]);
    });

  const setHidden = (nodes, hide) => nodes.forEach((n) => (n.hidden = hide));

  const apply = () => {
    let visible = 0;
    const visibleHrefs = new Set();
    cards.forEach((card) => {
      const ok = matches(card.meta);
      setHidden(card.nodes, !ok);
      const tocItem = tocById.get(card.meta.id);
      if (tocItem) tocItem.hidden = !ok;
      if (ok) {
        visible += 1;
        visibleHrefs.add("#" + card.meta.id);
      }
    });
    stages.forEach((stage) => {
      const anyVisible = stage.cards.some((card) => matches(card.meta));
      stage.heading.hidden = !anyVisible;
      setHidden(stage.intro, !anyVisible);
    });

    if (countEl) {
      countEl.textContent = `${visible} hébergement${visible > 1 ? "s" : ""} sur ${cards.length}`;
    }
    const anyActive = Object.values(active).some((set) => set.size);
    resets.forEach((reset) => (reset.hidden = !anyActive));
    if (emptyEl) emptyEl.hidden = visible !== 0;
    if (badge) {
      const activeCount = Object.values(active).reduce((n, s) => n + s.size, 0);
      badge.textContent = String(activeCount);
      badge.hidden = activeCount === 0;
    }
    updateMap(anyActive ? visibleHrefs : null);
  };

  pills.forEach((pill) => {
    pill.setAttribute("aria-pressed", "false");
    pill.addEventListener("click", () => {
      const { axis, value } = pill.dataset;
      active[axis] = active[axis] || new Set();
      const pressed = active[axis].has(value);
      if (pressed) active[axis].delete(value);
      else active[axis].add(value);
      pill.setAttribute("aria-pressed", pressed ? "false" : "true");
      apply();
    });
  });

  resets.forEach((reset) =>
    reset.addEventListener("click", () => {
      Object.values(active).forEach((set) => set.clear());
      pills.forEach((pill) => {
        pill.setAttribute("aria-pressed", "false");
      });
      apply();
    }),
  );

  applyCollapse(); // set collapse state before revealing (no flash on mobile)
  filters.hidden = false; // reveal once JS is available
  apply();
}

export function initRefugeFilter() {
  document.querySelectorAll("[data-refuges]").forEach(setupRoot);
}
